import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { onboardingService, OnboardingData } from '@/services/onboardingService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Info } from 'lucide-react';
import { CompanyService } from '@/services/companyService';
import { useCompany } from '@/hooks/useCompany';
import { userManagementApiClient } from '@/clients/userManagementApiClient';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { userFacingErrorMessage } from '@/utils/userFacingError';

export default function JoinCompany() {
  const t = useTranslations('consultantOnboarding');
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });
  const navigate = useNavigate();
  const { user, platformRole } = useAuth();
  // Self-serve company creation is staff-only; the real enforcement is server-side
  // (onboardingService.ts) — see specs/fin-521-remove-domain-restricted-invites/plan.md item 8.
  const canCreateCompany = platformRole === 'consultant' || platformRole === 'platform_admin';
  const { refetch: refetchCompany } = useCompany();
  const [isCheckingCompanies, setIsCheckingCompanies] = useState(true);
  const [formData, setFormData] = useState<OnboardingData>({
    organizationNumber: '',
    companyName: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingCompanies, setHasExistingCompanies] = useState(false);

  useEffect(() => {
    const checkExistingCompanies = async () => {
      try {
        // First, check for pending invitations and auto-accept them
        if (user?.email) {
          const invitationResult = await userManagementApiClient.checkAndAcceptPendingInvitations();

          if (invitationResult.success && invitationResult.data && invitationResult.data.acceptedCount > 0) {
            // Invitations were auto-accepted! Refetch company data and redirect
            const count = invitationResult.data.acceptedCount;
            toast.success(tRef.current('joinCompany.welcome', { count }));
            await refetchCompany();
            navigate('/overview');
            return; // Exit early, don't continue with the rest of the logic
          }
        }

        const companyService = new CompanyService();
        const companies = await companyService.getAllCompanies();
        setHasExistingCompanies(companies.length > 0);
      } catch (err) {
        console.error('Error checking existing companies:', err);
        setHasExistingCompanies(false);
      } finally {
        setIsCheckingCompanies(false);
      }
    };

    if (user?.email) {
      checkExistingCompanies();
    }
  }, [user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // TODO: Add validation for organization number
      // Validate organization number to be 10 digits and only digits
      if (!/^\d{10}$/.test(formData.organizationNumber)) {
        setError(t('joinCompany.orgNumberInvalid'));
        return;
      }
      const result = await onboardingService.onboardCompany(formData);

      if (result.success) {
        // Refetch company context to update the active company in the UI
        await refetchCompany();

        // Redirect to onboarding steps to complete KYC
        navigate('/onboarding-steps');
      } else {
        setError(result.error || t('joinCompany.onboardingFailed'));
      }
    } catch (err) {
      setError(userFacingErrorMessage(err, t('joinCompany.createFailed')));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof OnboardingData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    
    // For organization number, only allow digits and limit to 10
    if (field === 'organizationNumber') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // platformRole resolves via a separate async fetch in AuthContext, not tied to
  // viewMode's own checkExistingCompanies() effect — wait for it too, so a
  // consultant/platform_admin never flashes (or, if the role fetch is still
  // in flight when checkExistingCompanies() finishes, permanently sees) the
  // non-staff "No company access yet" message below.
  if (isCheckingCompanies || platformRole === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LogoutButton />
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canCreateCompany) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LogoutButton />
        <Card className="w-full max-w-md">
          <CardHeader>
            {hasExistingCompanies && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mb-2 w-fit"
                onClick={() => navigate('/overview')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('joinCompany.backToCompanies')}
              </Button>
            )}
            <CardTitle>{t('joinCompany.noAccess.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {t('joinCompany.noAccess.body')}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LogoutButton />
      <Card className="w-full max-w-md">
        <CardHeader>
          {hasExistingCompanies && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mb-2 w-fit"
              onClick={() => navigate('/overview')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('joinCompany.backToCompanies')}
            </Button>
          )}
          <CardTitle>{t('joinCompany.create.title')}</CardTitle>
          <CardDescription>
            {t('joinCompany.create.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="companyName">{t('joinCompany.create.companyName')}</Label>
              <Input
                id="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleInputChange('companyName')}
                placeholder={t('joinCompany.create.companyNamePlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organizationNumber">{t('joinCompany.create.organizationNumber')}</Label>
              <Input
                id="organizationNumber"
                type="text"
                value={formData.organizationNumber}
                onChange={handleInputChange('organizationNumber')}
                placeholder={t('joinCompany.create.organizationNumberPlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('joinCompany.create.creating')}
                </>
              ) : (
                t('joinCompany.create.submit')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
} 